/**
 * Global capture for the action log (#1895). Wires up click, form
 * submit, navigation, error, and visibility listeners. Call once at
 * app boot from main.tsx.
 *
 * Capture rules:
 *  - Clicks: nearest button/anchor's text + tag selector
 *  - Submits: form name/id + comma-joined field NAMES (never values)
 *  - Nav: pushState/replaceState/popstate — wrapped so React Router
 *    nav is captured even though it doesn't fire popstate
 *  - Errors: window.error with truncated message
 *  - Visibility: visibilitychange — handy for "did the rep tab away?"
 */

import { describeNode, logEvent } from './actionLog';

let installed = false;

export function installActionLog(): void {
  if (installed) return;
  installed = true;

  // ---- click ----
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target as Element | null;
      if (!el) return;
      const { target, selector } = describeNode(el);
      logEvent({ kind: 'click', target, selector });
    },
    true, // capture phase — fires before React's synthetic handlers
  );

  // ---- form submit ----
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target as HTMLFormElement | null;
      if (!form) return;
      const fieldNames: string[] = [];
      for (const el of Array.from(form.elements)) {
        const named = el as HTMLInputElement;
        if (named.name && fieldNames.length < 10) fieldNames.push(named.name);
      }
      logEvent({
        kind: 'submit',
        target: form.id || form.name || '<form>',
        note: fieldNames.length > 0 ? `fields: ${fieldNames.join(', ')}` : undefined,
      });
    },
    true,
  );

  // ---- navigation (pushState / replaceState / popstate) ----
  let lastPath = window.location.pathname + window.location.search;
  const recordNav = () => {
    const next = window.location.pathname + window.location.search;
    if (next !== lastPath) {
      logEvent({ kind: 'nav', from: lastPath, to: next });
      lastPath = next;
    }
  };
  window.addEventListener('popstate', recordNav);

  // React Router uses history.pushState/replaceState which do NOT fire
  // popstate. Wrap them so we still see the change. Use a microtask to
  // let the router finish its work before reading window.location.
  type HistoryMethod = 'pushState' | 'replaceState';
  const wrapHistory = (method: HistoryMethod) => {
    const original = history[method].bind(history);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (history as any)[method] = function (...args: unknown[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = (original as any)(...args);
      Promise.resolve().then(recordNav);
      return result;
    };
  };
  wrapHistory('pushState');
  wrapHistory('replaceState');

  // ---- uncaught errors ----
  window.addEventListener('error', (e: ErrorEvent) => {
    const message = (e.message || 'unknown error').slice(0, 120);
    const where = e.filename ? ` @ ${e.filename}:${e.lineno}` : '';
    logEvent({ kind: 'error', note: `${message}${where}` });
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
    logEvent({ kind: 'error', note: `unhandled: ${reason.slice(0, 120)}` });
  });

  // ---- visibility (tab away / come back) ----
  document.addEventListener('visibilitychange', () => {
    logEvent({ kind: 'visibility', note: document.visibilityState });
  });
}
